import { OTPInput, REGEXP_ONLY_DIGITS } from 'input-otp';

import { cn } from '../../utils/cn';
import { disabledDimStyle } from '../../utils/disabledDim';
import { subTestID } from '../../utils/testId';
import { CodeInputCells } from './CodeInput.cells';
import { CODE_INPUT_DEFAULT_LENGTH, type CodeInputProps } from './CodeInput.types';
import { activeRangeFromSlots } from './CodeInput.variants';

/**
 * Web one-time-code input — the joined cells from `CodeInput.cells.tsx`
 * driven by `input-otp`.
 *
 * The library, and not a hand-rolled six-`<input>` row, because everything
 * hard about this control is the part a screenshot does not show: a pasted
 * code landing in the right cells, `autoComplete="one-time-code"` autofill
 * filling all six at once, Backspace stepping back through empty cells,
 * arrow-key navigation, and the width nudge that stops password managers
 * planting their icon over the first cell. It is ONE real `<input>` under the
 * strip, which is also why the field has a value assistive tech and tests can
 * read.
 *
 * `dir="ltr"` on the wrapper: a code is a number, so the digits must read
 * left-to-right even in an RTL locale (he) where the surrounding card mirrors.
 */
export function CodeInput({
  value,
  onChangeText,
  onComplete,
  length = CODE_INPUT_DEFAULT_LENGTH,
  disabled = false,
  autoFocus = false,
  onFocus,
  onBlur,
  accessibilityLabel,
  testID,
  className,
  webProps,
  // Native-only escape hatches — accepted so the two forks share one prop
  // surface, ignored here.
  nativeProps: _nativeProps,
  nativeInputComponent: _nativeInputComponent,
}: CodeInputProps) {
  // `children` out of the pass-through: `OTPInput` types `render` and
  // `children` as mutually exclusive, and a `children?: ReactNode` arriving
  // through the spread would make the union unresolvable. The cells ARE the
  // children here; there is nothing for a caller to put inside.
  const { children: _children, ...inputProps } = webProps ?? {};
  return (
    <div
      dir="ltr"
      data-testid={testID}
      className={cn('flex w-full justify-center', className)}
      style={disabledDimStyle(disabled)}>
      <OTPInput
        // Spread FIRST, like the TextInput primitive does: the props below
        // are the component's contract (a controlled digits-only field), and
        // a caller's `name`/`form`/`data-*` must not be able to unseat them.
        {...inputProps}
        value={value}
        onChange={onChangeText}
        onComplete={onComplete}
        maxLength={length}
        // Rejects anything that isn't a digit at the source, so `onChangeText`
        // never sees one — the native fork strips them by hand for the same
        // contract.
        pattern={REGEXP_ONLY_DIGITS}
        inputMode="numeric"
        autoComplete="one-time-code"
        disabled={disabled}
        autoFocus={autoFocus}
        onFocus={onFocus}
        onBlur={onBlur}
        aria-label={accessibilityLabel}
        data-testid={subTestID(testID, 'input')}
        render={({ slots, isFocused }) => (
          <CodeInputCells
            testID={testID}
            chars={slots.map((slot) => slot.char)}
            // `isActive` is the library's own caret and selection tracking:
            // one slot for the caret, every selected slot after a select-all.
            // Unfocused, no cell is active.
            active={
              isFocused ? activeRangeFromSlots(slots.map((slot) => slot.isActive)) : null
            }
          />
        )}
      />
    </div>
  );
}
(CodeInput as { displayName?: string }).displayName = 'CodeInput';
